// Geocoding utility using Nominatim (OpenStreetMap)

interface GeocodingResult {
  lat: number;
  lng: number;
}

// Rate limiting - Nominatim requires max 1 request per second
let lastRequestTime = 0;

const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export async function geocodeAddress(
  address: string,
  postalCode: string,
  city: string
): Promise<GeocodingResult | null> {
  // Ensure we respect rate limit
  const now = Date.now();
  const timeSinceLastRequest = now - lastRequestTime;
  if (timeSinceLastRequest < 1100) {
    await wait(1100 - timeSinceLastRequest);
  }
  lastRequestTime = Date.now();

  try {
    const query = encodeURIComponent(`${address}, ${postalCode} ${city}, Finland`);
    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?format=json&q=${query}&limit=1`,
      {
        headers: {
          'User-Agent': 'CemeteryManagementApp/1.0'
        }
      }
    );
    
    if (!response.ok) {
      console.error('Geocoding request failed:', response.status);
      return null;
    }
    
    const data = await response.json();
    
    if (data.length > 0) {
      return {
        lat: parseFloat(data[0].lat),
        lng: parseFloat(data[0].lon)
      };
    }
    
    return null;
  } catch (error) {
    console.error('Geocoding error:', error);
    return null;
  }
}

export interface CemeteryToGeocode {
  id: string;
  address: string;
  postal_code: string;
  city: string;
  latitude: number | null;
  longitude: number | null;
}

export async function geocodeCemeteries(
  cemeteries: CemeteryToGeocode[],
  onUpdate?: (id: string, lat: number, lng: number) => Promise<void>
): Promise<Map<string, GeocodingResult>> {
  const results = new Map<string, GeocodingResult>();
  
  for (const cemetery of cemeteries) {
    // Skip if already has coordinates
    if (cemetery.latitude !== null && cemetery.longitude !== null) {
      results.set(cemetery.id, { lat: cemetery.latitude, lng: cemetery.longitude });
      continue;
    }
    
    // Try to geocode
    const coords = await geocodeAddress(cemetery.address, cemetery.postal_code, cemetery.city);
    if (coords) {
      results.set(cemetery.id, coords);
      // Optionally update database
      if (onUpdate) {
        await onUpdate(cemetery.id, coords.lat, coords.lng);
      }
    }
  }
  
  return results;
}
